<template>
    <b-modal id="mes-stats-caisse" :visible="value" title="Mes Stats" size="xl" scrollable hide-footer
        modal-class="mes-stats-modal" @hide="$emit('input', false)">
        <div class="mes-stats">
            <p class="mes-stats-intro">Activité encaissée sur votre caisse. Les versements, sorties et ventes annulées ne sont pas comptés.</p>

            <div class="mes-stats-filters">
                <div class="mes-stats-presets" aria-label="Période des statistiques">
                    <button v-for="option in presets" :key="option.id" type="button"
                        :class="['mes-stats-chip', { active: preset === option.id }]"
                        @click="selectPreset(option.id)">{{ option.label }}</button>
                </div>
                <label>Du <input v-model="fromDate" type="date" :max="toDate" @change="preset = 'custom'"></label>
                <label>Au <input v-model="toDate" type="date" :min="fromDate" @change="preset = 'custom'"></label>
                <button type="button" class="mes-stats-apply" :disabled="loading || !validDates" @click="loadStats">Afficher</button>
            </div>
            <p v-if="!validDates" class="mes-stats-error">Choisissez une période valide de 366 jours maximum.</p>
            <div v-if="loading" class="mes-stats-state" role="status">Chargement des statistiques…</div>
            <div v-else-if="error" class="mes-stats-state mes-stats-error" role="alert">{{ error }} <button type="button" @click="loadStats">Réessayer</button></div>
            <template v-else-if="stats">
                <div class="mes-stats-cards">
                    <div class="mes-stats-card"><span>Nombre de clients total</span><strong>{{ number(stats.clients_uniques) }}</strong><small>Patients distincts ayant acheté sur la période</small></div>
                    <div class="mes-stats-card"><span>Prestations vendues</span><strong>{{ number(totalQuantity) }}</strong><small>Lignes de vente distinctes encaissées</small></div>
                    <div class="mes-stats-card"><span>Ventes sans dossier</span><strong>{{ number(stats.lignes_sans_dossier) }}</strong><small>Comptées dans les ventes, pas dans les clients distincts</small></div>
                </div>

                <section class="mes-stats-section">
                    <h3>Montants Encaissés</h3>
                    <p>Sommes reçues par devise, sans addition ni conversion entre CDF et USD.</p>
                    <div class="mes-stats-total-grid">
                        <div v-for="item in totalsByCurrency" :key="item.currency" class="mes-stats-total">
                            <span>{{ item.currency === 'INCONNU' ? 'Devise à vérifier' : `Reçu en ${item.currency}` }}</span>
                            <strong>{{ number(item.amount) }} {{ moneyUnit(item.currency) }}</strong>
                            <small v-if="item.currency === 'INCONNU'">Anciens paiements non inclus dans les totaux CDF et USD.</small>
                        </div>
                    </div>
                </section>

                <section class="mes-stats-section" aria-labelledby="mes-stats-clients-title">
                    <div class="mes-stats-clients-header">
                        <h3 id="mes-stats-clients-title">Clients de la période <span>{{ number(clients.length) }}</span></h3>
                        <label v-if="clients.length > 12" class="mes-stats-client-search">
                            <span class="sr-only">Rechercher un client</span>
                            <input v-model="clientSearch" type="search" placeholder="Rechercher un client…">
                        </label>
                    </div>
                    <div v-if="visibleClients.length" class="mes-stats-table-wrap">
                        <table class="mes-stats-table mes-stats-clients-table" aria-labelledby="mes-stats-clients-title">
                            <thead><tr><th scope="col">N°</th><th scope="col">Client</th><th scope="col">Heures des transactions</th><th scope="col" class="number">Nombre de prestations</th></tr></thead>
                            <tbody>
                                <tr v-for="(client, index) in visibleClients" :key="client.id">
                                    <td>{{ (clientPage - 1) * 12 + index + 1 }}</td>
                                    <td>{{ client.nom }}</td>
                                    <td>
                                        <div v-if="client.heures_transactions && client.heures_transactions.length" class="mes-stats-client-times">
                                            <time v-for="(transaction, transactionIndex) in client.heures_transactions" :key="transactionIndex"
                                                :datetime="transaction.replace(' ', 'T')">{{ transactionTime(transaction) }}</time>
                                        </div>
                                        <span v-else>—</span>
                                    </td>
                                    <td class="number">{{ number(client.nombre_prestations) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="mes-stats-empty">{{ clientSearch ? 'Aucun client ne correspond à cette recherche.' : 'Aucun client avec dossier sur cette période.' }}</p>
                    <b-pagination v-if="filteredClients.length > 12" v-model="clientPage" :total-rows="filteredClients.length" :per-page="12"
                        aria-label="Pages des clients" class="mes-stats-clients-pages mb-0" />
                </section>

                <section class="mes-stats-section">
                    <h3>Prestations vendues</h3>
                    <div class="mes-stats-table-wrap">
                        <table class="mes-stats-table">
                            <thead><tr><th>Prestation</th><th>Catégorie</th><th class="number">Qté</th><th class="number">Encaissé</th></tr></thead>
                            <tbody>
                                <tr v-for="(row, index) in visiblePrestations" :key="index">
                                    <td>{{ row.name }}</td><td>{{ category(row.type) }}</td>
                                    <td class="number">{{ number(row.quantity) }}</td>
                                    <td class="number">{{ number(row.amount) }} {{ moneyUnit(row.currency) }}</td>
                                </tr>
                                <tr v-if="!groupedPrestations.length"><td colspan="4" class="mes-stats-empty">Aucune prestation pour cette période.</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <b-pagination v-if="groupedPrestations.length > 20" v-model="prestationPage" :total-rows="groupedPrestations.length" :per-page="20"
                        aria-label="Pages des prestations vendues" class="mes-stats-prestations-pages mb-0" />
                </section>

                <div class="mes-stats-columns">
                    <section class="mes-stats-section">
                        <h3>Catégories de ventes</h3>
                        <div v-for="(row, index) in groupedCategories" :key="index" class="mes-stats-list-row">
                            <span>{{ category(row.type) }}</span><strong>{{ number(row.amount) }} {{ moneyUnit(row.currency) }}</strong>
                        </div>
                        <div v-if="!groupedCategories.length" class="mes-stats-empty">Aucune donnée.</div>
                    </section>
                    <section class="mes-stats-section">
                        <h3>Moyens de paiement</h3>
                        <div v-for="(row, index) in groupedMethods" :key="index" class="mes-stats-list-row">
                            <span>{{ methodLabel(row.method) }}</span><strong>{{ number(row.amount) }} {{ moneyUnit(row.currency) }}</strong>
                        </div>
                        <div v-if="!groupedMethods.length" class="mes-stats-empty">Aucune donnée.</div>
                        <small>« Non précisé » correspond aux anciens tickets sans moyen de paiement enregistré.</small>
                    </section>
                </div>
                <section class="mes-stats-section">
                    <h3>Évolution par jour</h3>
                    <div class="mes-stats-table-wrap">
                        <table class="mes-stats-table">
                            <thead><tr><th>Date</th><th class="number">Encaissé</th></tr></thead>
                            <tbody>
                                <tr v-for="(row, index) in daily" :key="index"><td>{{ displayDate(row.jour) }}</td><td class="number">{{ number(row.montant) }} {{ moneyUnit(row.devise) }}</td></tr>
                                <tr v-if="!daily.length"><td colspan="2" class="mes-stats-empty">Aucune donnée.</td></tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </template>
        </div>
    </b-modal>
</template>

<script>
import operationsApi from '@/api/comptabilite/_operations'

function localDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export default {
    name: 'MesStats',
    props: { value: { type: Boolean, default: false }, devisePrincipale: { type: String, default: 'CDF' } },
    data() {
        const today = localDate(new Date())
        return {
            presets: [{ id: 'today', label: "Aujourd'hui" }, { id: 'week', label: '7 jours' }, { id: 'month', label: 'Ce mois' }],
            preset: 'today', fromDate: today, toDate: today,
            stats: null, loading: false, error: '', requestId: 0,
            clientSearch: '', clientPage: 1, prestationPage: 1
        }
    },
    watch: {
        value(open) { if (open) this.loadStats() },
        clientSearch() { this.clientPage = 1 }
    },
    computed: {
        clients() { return this.stats && Array.isArray(this.stats.clients) ? this.stats.clients : [] },
        filteredClients() {
            const normalize = text => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
            const query = normalize(this.clientSearch)
            return this.clients.filter(client => normalize(client.nom).includes(query))
        },
        visibleClients() { return this.filteredClients.slice((this.clientPage - 1) * 12, this.clientPage * 12) },
        validDates() {
            if (!this.fromDate || !this.toDate || this.fromDate > this.toDate) return false
            return (new Date(`${this.toDate}T12:00:00`) - new Date(`${this.fromDate}T12:00:00`)) / 86400000 <= 366
        },
        prestations() { return this.stats && Array.isArray(this.stats.prestations) ? this.stats.prestations : [] },
        daily() { return this.stats && Array.isArray(this.stats.evolution_journaliere) ? this.stats.evolution_journaliere : [] },
        totalQuantity() { return this.prestations.reduce((sum, row) => sum + Number(row.quantite || 0), 0) },
        totalsByCurrency() {
            const totals = this.aggregate(this.prestations,
                row => this.currency(row.devise), row => ({ currency: this.currency(row.devise) }))
            const currencies = ['CDF', 'USD']
            currencies.forEach(currency => {
                if (!totals.some(item => item.currency === currency)) totals.push({ currency, amount: 0 })
            })
            return totals.sort((a, b) => {
                const rank = currency => currencies.includes(currency) ? currencies.indexOf(currency) : currencies.length
                return rank(a.currency) - rank(b.currency)
            })
        },
        groupedPrestations() {
            return this.aggregate(this.prestations,
                row => [row.data_type, row.libelle, this.currency(row.devise)].join('|'),
                row => ({ name: row.libelle || 'Prestation sans libellé', type: row.data_type, currency: this.currency(row.devise) }))
                .sort((a, b) => b.amount - a.amount)
        },
        visiblePrestations() { return this.groupedPrestations.slice((this.prestationPage - 1) * 20, this.prestationPage * 20) },
        groupedCategories() {
            return this.aggregate(this.prestations,
                row => [row.data_type, this.currency(row.devise)].join('|'),
                row => ({ type: row.data_type, currency: this.currency(row.devise) }))
                .sort((a, b) => b.amount - a.amount)
        },
        groupedMethods() {
            return this.aggregate(this.prestations,
                row => [row.moyen || '', this.currency(row.devise)].join('|'),
                row => ({ method: row.moyen, currency: this.currency(row.devise) }))
                .sort((a, b) => b.amount - a.amount)
        }
    },
    methods: {
        selectPreset(id) {
            const now = new Date()
            this.preset = id
            this.toDate = localDate(now)
            if (id === 'today') this.fromDate = this.toDate
            if (id === 'week') { now.setDate(now.getDate() - 6); this.fromDate = localDate(now) }
            if (id === 'month') { now.setDate(1); this.fromDate = localDate(now) }
            this.loadStats()
        },
        async loadStats() {
            if (!this.validDates) return
            const requestId = ++this.requestId
            this.loading = true
            this.error = ''
            try {
                const response = await operationsApi.getMesStats({ from_date: this.fromDate, to_date: this.toDate })
                if (requestId !== this.requestId) return
                this.stats = response.data || response
                this.clientPage = 1
                this.prestationPage = 1
                this.clientSearch = ''
            } catch (error) {
                if (requestId !== this.requestId) return
                this.stats = null
                this.error = "Impossible de charger les statistiques de la caisse."
            } finally {
                if (requestId === this.requestId) this.loading = false
            }
        },
        aggregate(rows, keyFor, itemFor) {
            const result = new Map()
            rows.forEach(row => {
                const key = keyFor(row)
                if (!result.has(key)) result.set(key, { ...itemFor(row), amount: 0, quantity: 0 })
                const item = result.get(key)
                item.amount += Number(row.montant || 0)
                item.quantity += Number(row.quantite || 0)
            })
            return [...result.values()]
        },
        currency(value) { return String(value || this.devisePrincipale || 'CDF').trim().toUpperCase() },
        moneyUnit(value) { return this.currency(value) === 'INCONNU' ? `${this.currency()} équiv. (devise à vérifier)` : this.currency(value) },
        number(value) { return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(Number(value || 0)) },
        category(value) {
            return ({ bon_consultation: 'Consultations', consommable_bon: 'Consommables', analyse: 'Analyses', examen: 'Examens', pharmacie: 'Pharmacie', acte: 'Actes de soins' })[value] || value || 'Autres prestations'
        },
        methodLabel(value) { return ({ cash: 'Espèces', mobile_money: 'Mobile Money', cheque: 'Chèque', transfer: 'Virement' })[value] || value || 'Non précisé' },
        displayDate(value) {
            const parts = String(value || '').slice(0, 10).split('-')
            return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value
        },
        transactionTime(value) {
            const timestamp = String(value || '')
            const time = timestamp.slice(11, 16).replace(':', 'h')
            if (!time) return '—'
            return this.stats.from_date === this.stats.to_date
                ? time
                : `${this.displayDate(timestamp)} à ${time}`
        }
    }
}
</script>

<style lang="scss">
#mes-stats-caisse___BV_modal_backdrop_ {
    background-color: rgba(24, 35, 55, 0.35);
    opacity: 1;
    -webkit-backdrop-filter: blur(3px);
    backdrop-filter: blur(3px);
}
.mes-stats-modal .modal-dialog { width: calc(100vw - 32px); max-width: 1200px; }
.mes-stats-modal .modal-content { border: 0; border-radius: 20px; overflow: hidden; }
.mes-stats-modal .modal-header { padding: 24px 30px; border-bottom: 1px solid #dfe6f5; }
.mes-stats-modal .modal-title { font-size: 28px; font-weight: 700; color: var(--ui-text, #344468); }
.mes-stats-modal .modal-body { background: #eef3fa; padding: 26px 30px; }
.mes-stats { color: #172033; }
.mes-stats-clients-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.mes-stats-clients-header h3 { margin-bottom: 0; }
.mes-stats-clients-header h3 span { display: inline-block; border-radius: 99px; padding: 4px 10px; background: #eaf0ff; font-size: 14px; margin-left: 8px; }
.mes-stats-client-search { margin: 0; }
.mes-stats-client-search input { min-height: 38px; border: 1px solid #bdcbe0; border-radius: 10px; padding: 8px 12px; color: #172033; background: #fff; }
.mes-stats-clients-table th:first-child, .mes-stats-clients-table td:first-child { width: 64px; white-space: nowrap; }
.mes-stats-clients-table td:nth-child(2) { font-weight: 600; overflow-wrap: anywhere; }
.mes-stats-clients-table th:last-child, .mes-stats-clients-table td:last-child { width: 150px; }
.mes-stats-client-times { display: flex; flex-wrap: wrap; gap: 5px 8px; }
.mes-stats-client-times time { display: inline-block; padding: 3px 8px; border-radius: 8px; background: #eef4ff; color: #273449; white-space: nowrap; font-size: 13px; }
.mes-stats-clients-table tbody tr:nth-child(even) { background: #f8faff; }
.mes-stats-clients-pages { margin-top: 16px; }
.mes-stats-prestations-pages { margin-top: 16px; }
.mes-stats-intro { margin: 0 0 20px; color: var(--ui-text-secondary, #6d7b99); }
.mes-stats-filters { display: flex; gap: 12px; align-items: end; flex-wrap: wrap; margin-bottom: 22px; }
.mes-stats-presets { display: flex; gap: 8px; flex-wrap: wrap; margin-right: auto; }
.mes-stats-chip, .mes-stats-apply { border: 1px solid #ccdaf7; background: white; color: #4275ee; border-radius: 12px; padding: 10px 16px; }
.mes-stats-chip.active, .mes-stats-apply { color: white; background: #4275ee; border-color: #4275ee; }
.mes-stats-filters label { display: flex; flex-direction: column; gap: 5px; font-size: 13px; font-weight: 600; }
.mes-stats-filters input { border: 1px solid #ccdaf7; border-radius: 10px; padding: 9px; color: var(--ui-text, #344468); background: white; }
.mes-stats-apply:disabled { opacity: .55; }
.mes-stats-cards, .mes-stats-total-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.mes-stats-card, .mes-stats-section { background: white; border: 1px solid #e1e8f5; border-radius: 16px; padding: 20px; }
.mes-stats-card { display: flex; flex-direction: column; gap: 7px; }
.mes-stats-card span { font-weight: 600; }
.mes-stats-card strong { font-size: 27px; }
.mes-stats-card small, .mes-stats-section small, .mes-stats-section p { color: var(--ui-text-secondary, #71809c); }
.mes-stats-section { margin-top: 16px; }
.mes-stats-section h3 { font-size: 19px; font-weight: 700; margin-bottom: 12px; }
.mes-stats-section p { margin-top: -6px; }
.mes-stats-total-grid { grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
.mes-stats-total { display: flex; flex-direction: column; background: #eef4ff; border-radius: 10px; padding: 13px 15px; gap: 5px; }
.mes-stats-total strong { font-size: 20px; }
.mes-stats-columns { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.mes-stats-table-wrap { overflow-x: auto; }
.mes-stats-table { width: 100%; border-collapse: collapse; }
.mes-stats-table th { background: #f1f5fc; color: var(--ui-text, #52617f); }
.mes-stats-table th, .mes-stats-table td { padding: 11px 13px; border-bottom: 1px solid #e7edf7; }
.mes-stats-table .number { text-align: right; white-space: nowrap; }
.mes-stats-list-row { display: flex; justify-content: space-between; gap: 14px; border-bottom: 1px solid #e7edf7; padding: 10px 0; }
.mes-stats-list-row strong { white-space: nowrap; }
.mes-stats-state, .mes-stats-empty { text-align: center; padding: 24px; color: var(--ui-text-secondary, #71809c); }
.mes-stats-error { color: #b83b46; }
@media (max-width: 720px) {
    .mes-stats-modal .modal-body { padding: 18px; }
    .mes-stats-cards, .mes-stats-columns { grid-template-columns: 1fr; }
}
</style>
